import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { KIND_TO_PROTO } from '../../../components/SharedRecord/registryResolveClient';
import { registryRecordClient } from '../records/registryRecordClient';
// REG-61 part A: suggestions are REG-54 match candidates. These hooks read the
// status, ask the server to file a suggestion, and send the person's decision
// through DecideRegistryMatchCandidate; nothing here writes an identifier.
const QUEUED_POLL_MS = 5000;
export const QUEUED_POLL_MAX_MS = 2 * 60000;
export const identifierKeys = {
    status: (kind, id)=>[
            'registry-records',
            'identifier-status',
            kind,
            id
        ]
};
// Registry lists show the UEI, so a confirmed suggestion refreshes them.
const REGISTRY_LIST_KEYS = [
    'company profile',
    'companyProfile',
    'contract vehicle'
];
export function useIdentifierStatus(kind, id, enabled = true) {
    return useQuery({
        queryKey: identifierKeys.status(kind, id),
        enabled: enabled && id !== '',
        queryFn: async ()=>{
            const resp = await registryRecordClient.getRegistryIdentifierStatus({
                kind: KIND_TO_PROTO[kind],
                registryId: id
            });
            return resp.status ?? {
                status: ''
            };
        },
        // Poll only while a lookup is queued; the panel stops showing the spinner
        // after QUEUED_POLL_MAX_MS and the next focus refetch picks the result up.
        refetchInterval: (q)=>q.state.data?.status === 'QUEUED' ? QUEUED_POLL_MS : false
    });
}
export function useSuggestIdentifiers(kind, id) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (recheck)=>{
            const resp = await registryRecordClient.suggestRegistryIdentifiers({
                kind: KIND_TO_PROTO[kind],
                registryId: id,
                recheck
            });
            return resp.status;
        },
        onSuccess: (status)=>qc.setQueryData(identifierKeys.status(kind, id), status),
        onError: (e)=>toast.error(e instanceof Error ? e.message : 'Could not look that up.')
    });
}
export function useDecideIdentifierSuggestion(kind, id) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (a)=>registryRecordClient.decideRegistryMatchCandidate({
                candidateId: a.candidateId,
                decision: a.decision,
                chosenPublicKey: a.chosenPublicKey ?? '',
                expectedRevision: a.expectedRevision
            }),
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: identifierKeys.status(kind, id)
            });
            for (const key of REGISTRY_LIST_KEYS)qc.invalidateQueries({
                queryKey: [
                    key
                ]
            });
        }
    });
}
